import {
  Box,
  BoxProps,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  IconButton
} from "@chakra-ui/react"
import * as React from "react"
import { FiSend, FiUpload } from "react-icons/fi"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { SDKContext } from "../../../context/SDKContext"
import { useEmojiPicker } from "../../../hooks/useEmojiPicker"
import { roomSelector, brandingSelector } from "../../../recoil/atoms/room"
import { Icon } from "../../common/icons"
import * as Yup from "yup"
import useLocalUserHasPermissions from "../../../hooks/useLocalUserHasPermissions"
import { useTranslation } from "react-i18next"
import useSecretChatMessage from "../../../hooks/useSecretChatMessage"
import SelectedMessage from "../../servicesPanel/chat/_selectedMessage"
import {
  chatAttachmentDataAtom,
  chatAttachmentReplyDataAtom,
  chatsSelector,
  currentChatGenerationAtom,
  isSavingChatAttachment,
  isSavingChatAttachmentReply,
  previewAttachmentId,
  selectedReplyAtom
} from "../../../recoil/atoms/chats"
import { ElementId } from "src/constants/enums"
import { convertToBase64 } from "src/components/servicesPanel/chat/utils/base64"
import ChatAttachmentPreview from "src/components/servicesPanel/chat/_chatAttachmentPreview"
import { useIsVideoMimeType } from "src/components/servicesPanel/chat/utils/useIsVideo"
import { ToastConfigProps, useCustomToast } from "src/hooks/useToast"
import { addHitAreaStyle } from "src/util/styleUtils"
import { avatarCommunication } from "src/conditionals/Avatar/AvatarCommunication"
import useConfig from "src/hooks/useConfig"
import { useHandleAttachmentUpload } from "src/hooks/useHandleAttachmentUpload"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"

interface SendMessageProps extends BoxProps {
  isTransparent?: boolean
  isLandscape: boolean
  scrollToBottom?: () => void
  isAudience: boolean
  id: string
  isUrlBarNarrowed: boolean
  showTabsAtBottomOnMobile?: boolean
}

const MobileSendMessage = React.memo(
  ({
    isTransparent,
    scrollToBottom,
    isAudience,
    id,
    isUrlBarNarrowed,
    isLandscape,
    showTabsAtBottomOnMobile
  }: SendMessageProps) => {
    const { t } = useTranslation()
    const {
      showAttachmentInMvwOnSend,
      platformName,
      chatAttachment,
      chatAttachmentVideo
    } = useConfig()
    const room = useRecoilValue(roomSelector)
    const inputTextAreaRef = React.useRef(null)
    const isInputWasFocusedRef = React.useRef(false)
    const roomStyles = useRecoilValue(brandingSelector)
    const fileUploadRef = React.useRef<null | HTMLInputElement>(null)
    const avatarConfig = useCurrentAvatarInfo()

    const [attachment, setAttachment] = useRecoilState(chatAttachmentDataAtom)
    const [replyAttachment, setReplyAttachment] = useRecoilState(
      chatAttachmentReplyDataAtom
    )
    const { triggerWatchPartyOnUpload } = useHandleAttachmentUpload()
    const previewId = useRecoilValue(previewAttachmentId)
    const attachmentPreviewActive = !!previewId
    const setIsSavingAttachment = useSetRecoilState(isSavingChatAttachment)
    const [isFocus, setFocus] = React.useState<boolean>(true)
    const [text, setText] = React.useState("")
    const [error, setError] = React.useState("")
    const saving = useRecoilValue(isSavingChatAttachment)
    const savingReply = useRecoilValue(isSavingChatAttachmentReply)
    const allowList = `image/png, image/jpeg, image/gif, ${
      chatAttachmentVideo ? ".mp4, .mov," : ""
    }`
    const generation = useRecoilValue(currentChatGenerationAtom)

    React.useEffect(() => {
      if (!fileUploadRef.current) return
      if (attachment.data === "") {
        fileUploadRef.current.value = null
      }
      if (replyAttachment.data === "") {
        fileUploadRef.current.value = null
      }
    }, [attachment.data, replyAttachment.data])

    const { canSendChat, canUseChat } = useLocalUserHasPermissions()

    // User can send chat only if he has access to use and send the chat
    const hasChatSendPermission = canSendChat && canUseChat

    const schema = Yup.string()
      .max(1024, "max. 1024 char")
      .test("nospace", "", function (value) {
        const text = value.split("")
        const haveSpace = element => element !== ""
        return text.some(haveSpace)
      })
      .nullable()

    const meetingClient = React.useContext(SDKContext)

    const sendMessage = () => {}
    const { handleSecretChatMessage } = useSecretChatMessage()

    const [selectedReplyId, setSelectedReplyId] =
      useRecoilState(selectedReplyAtom)
    const showReplyMessage = !!selectedReplyId

    const chat = useRecoilValue(chatsSelector(selectedReplyId?.id))
    const reply =
      selectedReplyId?.replyId &&
      chat?.replyChats?.find(msg => msg.id === selectedReplyId?.replyId)

    // unselect the reply message if user goes fullscreen
    React.useEffect(() => {
      if (isTransparent) setSelectedReplyId(null)
    }, [isTransparent])

    /* Get last reply id which doesn't contain badReply */
    const getLastReplyId = React.useCallback(() => {
      if (chat?.replyChats.length !== 0) {
        for (let i = chat?.replyChats.length - 1; i >= 0; i--) {
          if (!chat?.replyChats[i].badReply) {
            return chat?.replyChats[i].id
          }
        }
      }
      return undefined
    }, [chat?.replyChats])

    /* Send reply message to backend */
    const sendReplyMessage = React.useCallback(
      (message: string, parentChatId: string) => {
        if (!message.length) return
        let immediateParentChatId
        if (parentChatId) {
          const lastReplyId = getLastReplyId()
          immediateParentChatId = lastReplyId ? lastReplyId : parentChatId
        }

        meetingClient?.sendPublicChat(
          message,
          parentChatId,
          "",
          immediateParentChatId,
          avatarConfig?.id.toString(),
          generation
        )
      },
      [getLastReplyId, meetingClient]
    )

    const focusInputField = () => {
      // if the input field was focused before clicking the send button, refocus.
      if (isInputWasFocusedRef.current) {
        inputTextAreaRef.current.focus()
        isInputWasFocusedRef.current = false
      }
    }

    const handleSendChatAttachment = (
      chatMessage: string,
      parentChatId?: string
    ) => {
      let immediateParentChatId
      if (parentChatId) {
        const lastReplyId = getLastReplyId()
        immediateParentChatId = lastReplyId ? lastReplyId : parentChatId
      }

      const attachmentData = parentChatId
        ? replyAttachment.data
        : attachment.data

      meetingClient?.sendPublicChatAttachment({
        chatMessage,
        attachment: attachmentData,
        userId: "",
        immediateParentChatId,
        parentChatId,
        toAvatarId: avatarConfig.id.toString(),
        generation
      })
      setIsSavingAttachment(true)

      // share the attachment on MVW and display avatar in popup
      if (showAttachmentInMvwOnSend) {
        triggerWatchPartyOnUpload()
      }
    }

    const handleChat = React.useCallback(async () => {
      try {
        if (text && text.length > 0 && text !== "") {
          const validText = await schema.validate(text)

          if (selectedReplyId && !attachment.data && !replyAttachment.data) {
            sendReplyMessage(text, selectedReplyId.id)
            setSelectedReplyId(null)
            setText(() => "")
            return
          } else if (
            selectedReplyId &&
            (attachment.data || replyAttachment.data)
          ) {
            handleSendChatAttachment(text, selectedReplyId.id)
            setSelectedReplyId(null)
            setText(() => "")
            return
          }

          const isSecretMessage = handleSecretChatMessage(validText)

          if (!isSecretMessage) {
            if (attachment.data) {
              handleSendChatAttachment(text)
            } else {
              meetingClient?.sendPublicChat(
                validText,
                null,
                null,
                null,
                avatarConfig?.id.toString(),
                generation
              )
            }
          }
          // communicate with the trulience iframe
          avatarCommunication.sendMessage(validText)

          setText(() => "")
        } else if (
          selectedReplyId &&
          (attachment.data || replyAttachment.data)
        ) {
          handleSendChatAttachment(text, selectedReplyId.id)
          setSelectedReplyId(null)
          setText(() => "")
          return
        } else if (attachment.data || replyAttachment.data) {
          handleSendChatAttachment(text)
        }
        focusInputField()
        setError("")
        if (scrollToBottom) {
          scrollToBottom()
        }
        // if (isTransparent) {
        // document.getElementById(id).blur()
        // setFocus(true)
        // }
      } catch (e) {
        setError(e.message)
      }
    }, [
      id,
      isTransparent,
      meetingClient,
      schema,
      scrollToBottom,
      text,
      handleSecretChatMessage,
      sendReplyMessage,
      selectedReplyId
    ])

    const handleKeyPress = React.useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
          event.preventDefault()
          handleChat()
        }
      },
      [handleChat]
    )

    const onEmojiClick = (event, emojiObject) => {
      setText(`${text}${emojiObject.emoji}`)
    }

    const { EmojiModal, closeModal } = useEmojiPicker(onEmojiClick)

    const iconColor = React.useMemo(() => {
      return isTransparent
        ? "white"
        : roomStyles?.servicePanelElementPrimaryText ?? "brand.800"
    }, [isTransparent, roomStyles?.servicePanelElementPrimaryText])

    const handleOnChange = React.useCallback(event => {
      // if (event.target.value.length > 0) {
      setText(event.target.value)
      // }
    }, [])

    const isDisableInput = React.useMemo(() => {
      return hasChatSendPermission ? false : true
    }, [hasChatSendPermission])

    const textPlaceholder = React.useMemo(() => {
      return hasChatSendPermission
        ? t("Send a message…")
        : t("Live chat currently disabled")
    }, [hasChatSendPermission, t])

    const stylesInput: React.CSSProperties = React.useMemo(() => {
      return {
        border: "2px solid",
        borderColor: error
          ? roomStyles?.servicePanelElementWarningText ?? "red"
          : roomStyles?.servicePanelElementBorder
          ? `${roomStyles?.servicePanelElementBorder}b3`
          : "gray",
        color:
          isTransparent || isLandscape
            ? "white"
            : room?.branding?.servicePanelElementPrimaryText ?? "gray",
        margin: "auto 0.5rem",
        paddingLeft: "0.5rem",
        paddingRight: hasChatSendPermission && "4.5rem",
        textAlign: !hasChatSendPermission && isAudience ? "left" : "unset",
        minHeight: "38px"
      }
    }, [
      error,
      roomStyles?.servicePanelElementWarningText,
      roomStyles?.servicePanelElementBorder,
      isTransparent,
      isLandscape,
      room?.branding?.servicePanelElementPrimaryText,
      hasChatSendPermission,
      isAudience
    ])

    const hoverStylesInput = React.useMemo(() => {
      return {
        borderColor: error
          ? roomStyles?.servicePanelElementWarningText ?? "red"
          : roomStyles?.servicePanelElementBorder ?? "gray"
      }
    }, [
      error,
      roomStyles?.servicePanelElementBorder,
      roomStyles?.servicePanelElementWarningText
    ])

    const placeholderStylesInput = React.useMemo(() => {
      return {
        color: isTransparent
          ? "white"
          : roomStyles?.servicePanelElementPrimaryText ?? "brand.800"
      }
    }, [isTransparent, roomStyles?.servicePanelElementPrimaryText])

    const focusBorderColor = React.useMemo(() => {
      return roomStyles?.servicePanelElementBorder ?? "gray"
    }, [roomStyles?.servicePanelElementBorder])

    const stylesContainerInput: React.CSSProperties = React.useMemo(() => {
      return {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        maxWidth: "100%",
        paddingTop:
          !attachment.data || !replyAttachment.data ? "0.375rem" : "0",
        /* paddingBottom: isTransparent ? "0.6rem" : "0.35rem", */
        width: "100%",
        zIndex: 10000,
        background:
          isTransparent || isLandscape || platformName === "Utta"
            ? "transparent"
            : room?.branding?.servicePanelColor ?? "white",
        marginBottom: `0px`
      }
    }, [
      isLandscape,
      isTransparent,
      room?.branding?.servicePanelColor,
      attachment,
      platformName
    ])

    React.useEffect(() => {
      if (!hasChatSendPermission) {
        setText("")
        setFocus(true)
        document.getElementById(id).blur()
        closeModal(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasChatSendPermission])

    React.useEffect(() => {
      if (showReplyMessage && !attachmentPreviewActive) {
        setAttachment({ data: "", mimeType: "" })
      }
    }, [showReplyMessage, attachmentPreviewActive])

    // const handleFocusForFsMode = () => {
    //   document.getElementById(id).focus()
    //   setFocus(false)
    // }

    const handleFileUploadClick = event => {
      event.preventDefault()
      setTimeout(function () {
        // Trigger the file input click after a short delay to avoid context menu displaying in wrong place
        fileUploadRef.current.click()
      }, 300)
    }

    const config: ToastConfigProps = {
      toastOptions: {
        containerStyle: {
          margin: "0.1rem"
        }
      },
      titleStyles: {
        fontWeight: 700
      },
      position: "bottom-right"
    }

    const showToastMessage = useCustomToast(config)

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.currentTarget.files[0]

      const SIX_MB_MAX_SIZE = 8 * 1024 * 1024 // 8MB
      const VIDEO_MAX_SIZE = 25 * 1024 * 1024 // 25MB

      if (!file) return

      const fileIsVideo = useIsVideoMimeType(file.type)
      const maxSize = fileIsVideo ? VIDEO_MAX_SIZE : SIX_MB_MAX_SIZE

      if (file.size > maxSize) {
        showToastMessage(
          `The ${
            fileIsVideo ? "video" : "image"
          } is too large. Maximum allowed size is ${maxSize / (1024 * 1024)}MB.`
        )
        return
      }

      if (selectedReplyId) {
        convertToBase64(e.currentTarget.files[0], setReplyAttachment)
      } else {
        convertToBase64(e.currentTarget.files[0], setAttachment)
      }
    }

    return (
      <Box>
        <Box position="relative" zIndex={10000}>
          <Box position="fixed" bottom={"60px"} width="100%">
            <EmojiModal />
          </Box>
        </Box>
        <form onSubmit={sendMessage} id={ElementId.MOBILE_CHAT_INPUT}>
          <Box
            style={stylesContainerInput}
            paddingBottom={
              isUrlBarNarrowed && !showTabsAtBottomOnMobile
                ? "1.7rem"
                : isTransparent
                ? "0.6rem"
                : showTabsAtBottomOnMobile
                ? "0.375rem"
                : "0.5rem"
            }
            display="flex"
            flexDir="column"
            borderTop={attachment.data ? "2px solid" : "none"}
            borderColor={roomStyles.badgeBorderColor}
          >
            {attachment.data && (
              <ChatAttachmentPreview
                attachment={attachment}
                removeAttachment={() => {
                  setAttachment({ data: "", mimeType: "" })
                }}
                roomStyles={roomStyles}
                borderColor={
                  roomStyles?.servicePanelElementBorder
                    ? `${roomStyles?.servicePanelElementBorder}b3`
                    : "gray"
                }
                saving={saving}
              />
            )}
            {replyAttachment.data && (
              <ChatAttachmentPreview
                attachment={replyAttachment}
                removeAttachment={() => {
                  setReplyAttachment({ data: "", mimeType: "" })
                }}
                roomStyles={roomStyles}
                borderColor={
                  roomStyles?.servicePanelElementBorder
                    ? `${roomStyles?.servicePanelElementBorder}b3`
                    : "gray"
                }
                saving={savingReply}
              />
            )}
            {error && (
              <Box
                right="0px"
                top={"-15px"}
                pos="absolute"
                fontSize="0.7rem"
                color={roomStyles?.servicePanelElementWarningText ?? "red"}
                marginRight="1rem"
              >
                {error}
              </Box>
            )}
            <InputGroup>
              {showReplyMessage && !attachmentPreviewActive && (
                <Box
                  paddingX="4"
                  position={"absolute"}
                  width="100%"
                  zIndex={10000}
                >
                  <SelectedMessage
                    chat={reply ?? chat}
                    isMobile={true}
                    isTransparent={false}
                    handleClose={() => {
                      setSelectedReplyId(null)
                      setReplyAttachment({ data: "", mimeType: "" })
                    }}
                  />
                </Box>
              )}

              {isFocus && text.length === 0 && (
                <Text
                  paddingTop={
                    showReplyMessage && !attachmentPreviewActive && "60px"
                  }
                  transform="translate(0%, -50%)"
                  top="50%"
                  zIndex="1000000"
                  color={
                    isTransparent || isLandscape
                      ? "white"
                      : room?.branding?.servicePanelElementPrimaryText ?? "gray"
                  }
                  opacity="0.5"
                  position="absolute"
                  left={"20px"}
                  onClick={() => {
                    document.getElementById(id).focus()
                  }}
                  // onTouchStart={
                  //   isTransparent ? handleFocusForFsMode : undefined
                  // }
                >
                  {textPlaceholder}
                </Text>
              )}
              <Input
                id={id}
                ref={inputTextAreaRef}
                // onTouchStart={isTransparent ? handleFocusForFsMode : undefined}
                autoCorrect="off"
                height={showReplyMessage && !attachmentPreviewActive && "100px"}
                paddingTop={
                  showReplyMessage && !attachmentPreviewActive && "60px"
                }
                onChange={handleOnChange}
                // onFocus={onFocus}
                enterKeyHint="send"
                onKeyPress={handleKeyPress}
                style={stylesInput}
                focusBorderColor={focusBorderColor}
                isDisabled={isDisableInput}
                background={
                  isTransparent || isLandscape
                    ? "rgba(0, 0, 0, 0.5) !important"
                    : room?.branding?.servicePanelElementBackground ?? "white"
                }
                pl="4"
                value={text}
                rows={1}
                _placeholder={placeholderStylesInput}
                _hover={hoverStylesInput}
              />
              {hasChatSendPermission && (
                <InputRightElement
                  onMouseDown={() => {
                    // Save the focus state of the input, for refocusing after clicking send button
                    isInputWasFocusedRef.current =
                      document.activeElement === inputTextAreaRef.current
                  }}
                  right={isTransparent ? "12px" : "18px"}
                  paddingTop={showReplyMessage && "80px"}
                  width="54px"
                  justifyContent="space-between"
                  children={
                    <>
                      <IconButton
                        aria-label="Upload an attachment"
                        icon={
                          <FiUpload
                            color="gray.500"
                            size="1.2em"
                            style={{ margin: 0 }}
                          />
                        }
                        variant="link"
                        opacity={text.length > 0 ? 1 : 0.75}
                        color={
                          roomStyles?.servicePanelElementPrimaryText ??
                          "gray.500"
                        }
                        _focus={{}}
                        onClick={handleFileUploadClick}
                        minW="unset"
                        {...addHitAreaStyle({ left: 8, top: 8, bottom: 8 })}
                      />
                      <IconButton
                        aria-label="Send message"
                        icon={
                          <Icon
                            aria-label="Send message to chat"
                            src={FiSend}
                            color={iconColor}
                            fontSize="xl"
                          />
                        }
                        variant="link"
                        opacity={text.length > 0 ? 1 : 0.75}
                        color={
                          roomStyles?.servicePanelElementPrimaryText ??
                          "gray.500"
                        }
                        _focus={{}}
                        onClick={e => {
                          e.stopPropagation()
                          handleChat()
                        }}
                        minW="unset"
                        {...addHitAreaStyle({ right: 10, top: 8, bottom: 8 })}
                      />
                    </>
                  }
                />
              )}
            </InputGroup>
          </Box>
        </form>
        {hasChatSendPermission && (
          <input
            ref={fileUploadRef}
            type="file"
            accept={allowList}
            onChange={handleFileUpload}
            style={{
              width: 0,
              height: 0,
              overflow: "hidden",
              position: "absolute"
            }}
          />
        )}
      </Box>
    )
  }
)

export default MobileSendMessage
